import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, usersApi, handoffApi, setTokens, clearTokens, isAuthenticated } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ✅ FIX: Add ref to prevent multiple initialization calls
  const initializingRef = useRef(false);
  const handoffProcessedRef = useRef(false);

  // Handle handoff code from URL parameters
  const handleHandoffCode = useCallback(async () => {
    // Only process handoff code once
    if (handoffProcessedRef.current) {
      return false;
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const handoffCode = urlParams.get('handoff_code');
      
      if (!handoffCode) {
        return false;
      }

      console.log('AuthProvider: Found handoff_code in URL, exchanging...');
      handoffProcessedRef.current = true;
      setLoading(true);
      setError(null);

      // Exchange handoff code for tokens
      const response = await handoffApi.exchangeHandoffCode(handoffCode);
      
      console.log('AuthProvider: Handoff code exchanged successfully');
      
      // Store tokens and user
      setTokens(response.access_token, response.refresh_token);
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Remove handoff_code from URL without page reload
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('handoff_code');
      window.history.replaceState({}, '', newUrl.toString());
      
      console.log('AuthProvider: Handoff authentication complete');
      return true;
    } catch (error) {
      console.error('AuthProvider: Handoff code exchange failed:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to authenticate with handoff code');
      
      // Remove invalid handoff_code from URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('handoff_code');
      window.history.replaceState({}, '', newUrl.toString());
      
      handoffProcessedRef.current = false;
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ FIX: Wrap initializeAuth in useCallback to prevent recreation
  const initializeAuth = useCallback(async () => {
    // ✅ FIX: Prevent multiple simultaneous calls
    if (initializingRef.current) {
      console.log('AuthProvider: Already initializing, skipping...');
      return;
    }

    try {
      initializingRef.current = true;
      setLoading(true);
      setError(null);

      console.log('AuthProvider: Checking localStorage for user and tokens...');
      
      // Check if user is stored in localStorage
      const storedUser = localStorage.getItem('user');
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      console.log('AuthProvider: Found stored data:', {
        hasUser: !!storedUser,
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        isAuthenticated: isAuthenticated()
      });

      if (storedUser && accessToken && refreshToken) {
        const userData = JSON.parse(storedUser);
        console.log('AuthProvider: Setting user from localStorage:', userData);
        
        // ✅ FIX: Check if token is expiring soon and refresh proactively
        const { isTokenExpiringSoon } = await import('../utils/tokenManager');
        if (isTokenExpiringSoon(accessToken)) {
          console.log('AuthProvider: Token expiring soon, refreshing...');
          try {
            await authApi.refresh();
            console.log('AuthProvider: Token refreshed successfully');
          } catch (error) {
            console.error('AuthProvider: Proactive token refresh failed:', error);
            logout();
            return;
          }
        }
        
        setUser(userData);
        
        // Verify token is still valid by fetching current user
        try {
          console.log('AuthProvider: Verifying token with /users/me...');
          const currentUser = await usersApi.getMe();
          console.log('AuthProvider: Token valid, updating user data:', currentUser);
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (error) {
          console.error('AuthProvider: Token verification failed:', error);
          // Token invalid, clear auth
          logout();
        }
      } else {
        // No user or no token - clear everything and show login
        console.log('AuthProvider: No valid stored auth data found');
        setUser(null); // Explicitly set to null
      }
    } catch (error) {
      console.error('AuthProvider: Auth initialization error:', error);
      logout();
    } finally {
      console.log('AuthProvider: Auth initialization complete');
      setLoading(false);
      initializingRef.current = false;
    }
  }, []); // ✅ FIX: Empty dependency array since this should only be created once

  // ✅ FIX: Initialize auth state on mount with proper dependency
  useEffect(() => {
    const init = async () => {
      console.log('AuthProvider: Initializing auth...');
      
      // First, check for handoff code in URL
      const handoffSuccess = await handleHandoffCode();
      
      // If handoff code was processed successfully, skip normal initialization
      if (!handoffSuccess) {
        await initializeAuth();
      }
    };
    
    init();
  }, [initializeAuth, handleHandoffCode]); // ✅ FIX: Add dependencies

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      console.log('AuthProvider: Attempting login...');
      const response = await authApi.login(credentials);
      console.log('AuthProvider: Login successful:', response);
      
      // The authApi.login already saves tokens and user to localStorage
      // We just need to update the state
      setUser(response.user);
      
      return response;
    } catch (error) {
      console.error('AuthProvider: Login failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (error) {
      console.error('AuthProvider: Logout error:', error);
    } finally {
      console.log('AuthProvider: Clearing auth state');
      clearTokens();
      setUser(null);
      setError(null);
    }
  }, []); // ✅ FIX: Add useCallback with empty deps

  const updateUser = useCallback((userData) => {
    console.log('AuthProvider: Updating user data:', userData);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []); // ✅ FIX: Add useCallback

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};